// Сведение судов: обращение к своему разделу ядра через BFF. ТОЛЬКО ЧТЕНИЕ.
//
// Устроено как api/dq.ts и api/xreports.ts: свой БАЗА, своя функция запроса, хуки рядом.
// Общего клиента здесь нет намеренно — api/client.ts обслуживает эмуляцию Bitrix REST
// (POST /api/bx), а у модулей ядра форма ответа другая.
//
// ПИСАТЬ ОТСЮДА НЕЛЬЗЯ И НЕ БУДЕТ. Свести карточки можно только в Bitrix, руками. Причина
// не в осторожности вообще: перевешивание сделки — это `crm.deal.update`, а на изменении
// сделки автоматом стартуют БП 685, 52 и 2723, которые ставят задачи живым людям.
// Кнопки «свести» здесь нет и появиться не должна.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import type { ОтветГрупп, ОтветГруппы, ОтветСправочника } from '../lib/vessel-merge'

const БАЗА = '/api/vessel-merge'

/** Отказ по правам. Отдельный класс, а не общая ошибка: 403 здесь не поломка, а
 *  нормальный ответ сервера, и выглядеть он должен объяснением, а не строкой «Ошибка». */
export class НетДоступа extends Error {
  constructor(msg = 'forbidden') {
    super(msg)
    this.name = 'НетДоступа'
  }
}

/** Тот же разбор ответа, что в api/dq.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  if (res.status === 403) throw new НетДоступа()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 — неизвестный IMO или негодный параметр. Его надо ОБЪЯСНИТЬ, а не показать
    // пустую таблицу: пустая таблица здесь читается как «дублей нет», и человек уйдёт.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// Группы пересчитываются синком раз в несколько часов, а не поминутно: держать их
// свежими чаще незачем. Пять минут staleTime затем, чтобы переключение фильтров и
// порядка не ходило в сеть — и то и другое считается на клиенте (lib/vessel-merge).
const СВОД = { staleTime: 5 * 60_000, retry: 1 } as const
const ГРУППА = { staleTime: 60_000, retry: 1 } as const

export function useСправочникСведения(включён = true) {
  return useQuery<ОтветСправочника>({
    queryKey: ['vessel-merge', 'refs'],
    queryFn: () => запрос<ОтветСправочника>('/refs'),
    enabled: включён,
    ...СВОД,
  })
}

/** Все группы одной страницей: их 357, и на клиенте они отбираются без похода в сеть. */
export function useГруппыДублей(limit = 400, включён = true) {
  return useQuery<ОтветГрупп>({
    queryKey: ['vessel-merge', 'groups', limit],
    queryFn: () => запрос<ОтветГрупп>(`/groups?limit=${limit}`),
    enabled: включён,
    ...СВОД,
  })
}

/** Детали одной группы. `imo` пустой — не спрашиваем вовсе. */
export function useГруппуДублей(imo: string, включён = true) {
  return useQuery<ОтветГруппы>({
    queryKey: ['vessel-merge', 'group', imo],
    queryFn: () => запрос<ОтветГруппы>(`/group?imo=${encodeURIComponent(imo)}`),
    enabled: включён && !!imo,
    ...ГРУППА,
  })
}
